import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { Icon } from "./adminIcons";

const TONE = { amber: "text-amber-300 bg-amber-900/30 border-amber-700/40", rose: "text-rose-300 bg-rose-900/30 border-rose-700/40", sky: "text-sky-300 bg-sky-900/30 border-sky-700/40" };
const tip = { background: "#1A090D", border: "1px solid #781826", borderRadius: 12, color: "#FAF5F0", fontSize: 12 };

export default function AdminCommandCenter() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/command-center").then(({ data }) => setD(data)); }, []);
  if (!d) return <FullSpinner label="Loading command center…" />;
  const operational = (d.platform_state?.mode || "operational") === "operational";

  return (
    <div className="space-y-6" data-testid="admin-command-center">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold">Command Center</h1>
          <p className="text-sm text-[#D8B4B9] mt-1">The live operating view of ManaJodi — every number below is queried from the database.</p>
        </div>
        <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${operational ? "text-emerald-300 bg-emerald-900/30 border-emerald-700/40" : "text-amber-300 bg-amber-900/30 border-amber-700/40"}`} data-testid="platform-status-pill">
          <span className={`w-2 h-2 rounded-full ${operational ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
          {operational ? "All systems operational" : `Platform: ${d.platform_state.mode}`}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {d.kpis.map((k) => (
          <div key={k.label} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Icon name={k.icon} className="w-5 h-5 text-[#C59B27] mb-2" />
            <div className="font-serif text-2xl font-bold text-[#E2C382]">{k.value}</div>
            <div className="text-xs text-[#D8B4B9]">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Trends */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
            <h2 className="font-serif text-lg font-semibold mb-4">Registrations · last 14 days</h2>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={d.trends.registrations}>
                <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C59B27" stopOpacity={0.5} /><stop offset="100%" stopColor="#C59B27" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#781826" opacity={0.2} />
                <XAxis dataKey="date" stroke="#8A6D71" fontSize={11} />
                <Tooltip contentStyle={tip} />
                <Area type="monotone" dataKey="count" stroke="#C59B27" strokeWidth={2} fill="url(#rg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
              <h2 className="font-serif text-base font-semibold mb-3">Interests · 14 days</h2>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={d.trends.interests}><XAxis dataKey="date" stroke="#8A6D71" fontSize={10} /><Tooltip contentStyle={tip} /><Bar dataKey="count" fill="#781826" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
              <h2 className="font-serif text-base font-semibold mb-3">Revenue ₹ · 14 days</h2>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={d.trends.revenue}><XAxis dataKey="date" stroke="#8A6D71" fontSize={10} /><Tooltip contentStyle={tip} /><Bar dataKey="count" fill="#E2C382" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5" data-testid="needs-attention">
            <h2 className="font-serif text-lg font-semibold mb-3">Needs Attention</h2>
            {d.needs_attention.length === 0 ? (
              <p className="text-sm text-emerald-300">Nothing needs you right now — automation has it covered. ✦</p>
            ) : (
              <div className="space-y-2">
                {d.needs_attention.map((n) => (
                  <Link key={n.label} to={n.link} data-testid={`attention-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm hover:opacity-90 ${TONE[n.tone] || TONE.sky}`}>
                    <span>{n.label}</span>
                    <span className="font-bold">{n.count}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
            <h2 className="font-serif text-lg font-semibold mb-3">System Health</h2>
            <div className="space-y-2">
              {d.health.map((h) => (
                <div key={h.name} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${h.ok ? "bg-emerald-400" : "bg-rose-400"}`} />{h.name}</span>
                  <span className="text-xs text-[#8A6D71]">{h.detail}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
            <h2 className="font-serif text-lg font-semibold mb-3">Live Activity</h2>
            <div className="space-y-2.5 max-h-72 overflow-y-auto">
              {d.activity.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.type === "payment" ? "bg-[#E2C382]" : a.type === "admin" ? "bg-sky-400" : "bg-[#781826]"}`} />
                  <div className="flex-1"><span className="text-[#FAF5F0]">{a.text}</span>{a.code && <span className="font-mono text-[#8A6D71]"> · {a.code}</span>}<div className="text-[#8A6D71]">{a.at ? new Date(a.at).toLocaleString() : ""}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
